import type { TaskRunEvent } from "@mlx/contracts";

export type RunSourceKind =
  | "twin"
  | "web"
  | "plugin"
  | "warehouse"
  | "workspace"
  | "memory";

export type RunSourceUsage = {
  key: string;
  kind: RunSourceKind;
  label: string;
  provider?: string;
  pluginSlug?: string;
  /** The tables a warehouse source read, for its hover ("Xero · Invoices, Contacts"). */
  tables?: string[];
  callCount: number;
  documentCount: number;
  searchCount: number;
  fetchCount: number;
};

type ToolContext = {
  pluginSlug?: string;
  pluginLabel?: string;
  /** The tables a warehouse call read, from its SQL or its table argument. */
  tables?: string[];
};

/** MLX's own plugins (its warehouse, workspace, artifacts): an answer names what they read, never their name. */
const MLX_PLUGIN = /^mlx-/;
const VENDOR_WORDS: Record<string, string> = { xero: "Xero", hubspot: "HubSpot", gmail: "Gmail", github: "GitHub", slack: "Slack", google: "Google" };

/** The tables a warehouse call reads ("xero_invoice_lines"), schema left off; catalogue tables are not the user's data. */
export function warehouseTablesRead(args: Record<string, unknown> | undefined): string[] {
  const identifier = String.raw`(?:"[^"]+"|\w+)`;
  // A FROM inside EXTRACT(YEAR FROM invoice_date), SUBSTRING(…), TRIM(…) or the like names a column, not a table.
  const inFunction = String.raw`(?<!\b(?:extract|substring|substr|trim|overlay|position)\s*\([^()]*)`;
  const tableName = new RegExp(String.raw`${inFunction}\b(?:from|join)\s+(${identifier}(?:\.${identifier})*)`, "giu");
  const named = [readString(args?.table), ...[...(readString(args?.sql) ?? "").matchAll(tableName)].map((match) => match[1])];
  return [...new Set(named.flatMap((name) => {
    const table = name?.replaceAll('"', "").split(".").at(-1)?.toLowerCase();
    return table && !table.startsWith("pg_") && !name!.toLowerCase().includes("information_schema") ? [table] : [];
  }))];
}

/** Whether a plugin is MLX's own (its warehouse, workspace, artifacts), which an answer never names. */
export function isMlxPlugin(slug: string): boolean {
  return MLX_PLUGIN.test(slug);
}

/** A plugin's name in an answer's sources: its own, except MLX's, which read by what they hold ("Workspace"). */
function pluginSourceLabel(slug: string, label: string | undefined): string {
  return isMlxPlugin(slug) ? humanize(slug.replace(MLX_PLUGIN, "")) : (label ?? humanize(slug));
}

/**
 * A warehouse call's sources: one per system its tables came from (the tables "xero_invoices" and "xero_contacts" are
 * Xero, drawn with Xero's mark), so an answer reads "Twin, Xero" and its hover lists the tables. Tables of the
 * person's own read as "Your data".
 */
function warehouseSources(tables: readonly string[]): Array<Omit<RunSourceUsage, "callCount" | "documentCount" | "searchCount" | "fetchCount">> {
  const bySystem = new Map<string | null, string[]>();
  for (const table of tables) {
    const system = tableSystem(table);
    bySystem.set(system, [...(bySystem.get(system) ?? []), table]);
  }
  if (bySystem.size === 0) bySystem.set(null, []);
  return [...bySystem].map(([system, read]) => system
    // Keyed as the system's own plugin is, so a run that also called Xero directly shows one Xero.
    ? { key: `plugin:${system}`, kind: "warehouse" as const, label: VENDOR_WORDS[system]!, pluginSlug: system, tables: read }
    : { key: "warehouse", kind: "warehouse" as const, label: "Your data", tables: read });
}

/** The system a table's data came from ("xero_invoices" → "xero"), or null for the person's own. */
export function tableSystem(table: string): string | null {
  const first = table.split(/[_-]+/u)[0]!;
  return VENDOR_WORDS[first] ? first : null;
}

/** A table as the person knows its data: "xero_invoice_lines" → "Xero invoice lines". */
export function warehouseTableLabel(table: string): string {
  const words = table.split(/[_-]+/u).filter(Boolean).map((word) => VENDOR_WORDS[word] ?? word);
  const text = words.join(" ");
  return text ? text[0]!.toUpperCase() + text.slice(1) : table;
}

const WORKSPACE_SOURCE_TOOL =
  /^twin_workspace_(?:aggregate|extract|get|list|query|read|search)_/;

// The consolidated CLI-tool lane emits tool events without pluginSlug; a
// registry-backed resolver lets provenance still attribute those calls.
export type ResolveToolPlugin = (
  toolName: string,
) => { pluginSlug: string; pluginLabel?: string } | undefined;

export function buildRunSourceUsage(
  events: TaskRunEvent[] | undefined,
  resolveToolPlugin?: ResolveToolPlugin,
): RunSourceUsage[] {
  const contextByCall = new Map<string, ToolContext>();
  for (const event of events ?? []) {
    if (event.kind !== "tool.started" && event.kind !== "tool.updated")
      continue;
    const payload = readRecord(event.payload);
    const toolCallId = readString(payload?.toolCallId);
    if (!toolCallId) continue;
    const existing = contextByCall.get(toolCallId);
    const tables = warehouseTablesRead(readRecord(payload?.args));
    contextByCall.set(toolCallId, {
      pluginSlug: readString(payload?.pluginSlug) ?? existing?.pluginSlug,
      pluginLabel: readString(payload?.pluginLabel) ?? existing?.pluginLabel,
      tables: tables.length ? tables : existing?.tables,
    });
  }

  const usageByKey = new Map<string, RunSourceUsage>();
  for (const event of events ?? []) {
    if (event.kind !== "tool.completed") continue;
    const payload = readRecord(event.payload);
    if (!payload || payload.isError === true) continue;
    const toolName = readString(payload.toolName);
    if (!toolName) continue;
    const context = contextByCall.get(readString(payload.toolCallId) ?? "");
    const pluginSlug = readString(payload.pluginSlug) ?? context?.pluginSlug;
    // A warehouse call is each table it read, by the data's own name; one with no table read is the person's data. One
    // that names the system whose data it read (Google Drive's) is that system's.
    const warehouse = (toolName.startsWith("warehouse_") || pluginSlug === "mlx-warehouse")
      && (!pluginSlug || isMlxPlugin(pluginSlug));
    // Listing the warehouse's tables reads none of the person's data, so it isn't a source.
    if (toolName === "warehouse_list_tables") continue;
    const sources = warehouse
      ? warehouseSources(context?.tables ?? [])
      : [classifySource({
        toolName,
        provider: readString(payload.provider),
        pluginSlug,
        pluginLabel: readString(payload.pluginLabel) ?? context?.pluginLabel,
        resolveToolPlugin,
      })];
    const usage = readRecord(payload.usage);
    for (const source of sources) {
      if (!source) continue;
      const existing = usageByKey.get(source.key);
      if (existing) {
        if (source.tables) existing.tables = [...new Set([...(existing.tables ?? []), ...source.tables])];
        existing.callCount += 1;
        existing.documentCount += readCount(usage?.documentCount);
        existing.searchCount += readCount(usage?.searchRequestCount);
        existing.fetchCount += readCount(usage?.fetchRequestCount);
        continue;
      }
      usageByKey.set(source.key, {
        ...source,
        callCount: 1,
        documentCount: readCount(usage?.documentCount),
        searchCount: readCount(usage?.searchRequestCount),
        fetchCount: readCount(usage?.fetchRequestCount),
      });
    }
  }

  const sources = [...usageByKey.values()];
  if (sources.length === 0) return [];
  const twinIndex = sources.findIndex((source) => source.kind === "twin");
  if (twinIndex >= 0) {
    if (twinIndex > 0) {
      const [twin] = sources.splice(twinIndex, 1);
      sources.unshift(twin!);
    }
    return sources;
  }
  sources.unshift({
    key: "twin",
    kind: "twin",
    label: "Twin",
    callCount: 0,
    documentCount: 0,
    searchCount: 0,
    fetchCount: 0,
  });
  return sources;
}

export function runUsedTwin(
  events: TaskRunEvent[] | undefined,
  resolveToolPlugin?: ResolveToolPlugin,
): boolean {
  return buildRunSourceUsage(events, resolveToolPlugin).some(
    (source) => source.kind === "twin",
  );
}

export function sourceUsageMetric(source: RunSourceUsage): string {
  if (source.kind === "twin" && source.callCount < 1) return "consulted";
  if (source.kind === "web" && source.documentCount > 0) {
    return `${source.documentCount} source${source.documentCount === 1 ? "" : "s"}`;
  }
  const [singular, plural] =
    source.kind === "warehouse"
      ? ["query", "queries"]
      : source.kind === "workspace" ||
          source.kind === "memory" ||
          source.kind === "twin"
        ? ["lookup", "lookups"]
        : ["call", "calls"];
  return `${source.callCount} ${source.callCount === 1 ? singular : plural}`;
}

export function sourceUsageDescription(source: RunSourceUsage): string {
  if (source.kind === "twin" && source.callCount < 1) {
    return "The Twin was consulted for this retrieval.";
  }
  if (source.kind !== "web") {
    // A warehouse source names the tables it read, without repeating the system: "Xero · Invoices, Contacts · 3 queries".
    const vendor = Object.keys(VENDOR_WORDS).find((key) => VENDOR_WORDS[key] === source.label);
    const tables = (source.tables ?? []).map((table) =>
      warehouseTableLabel(vendor ? table.replace(new RegExp(`^${vendor}[_-]+`, "u"), "") : table));
    return [source.label, tables.length ? tables.join(", ") : null, sourceUsageMetric(source)].filter(Boolean).join(" · ");
  }
  const operations = [
    source.searchCount > 0
      ? `${source.searchCount} search${source.searchCount === 1 ? "" : "es"}`
      : undefined,
    source.fetchCount > 0
      ? `${source.fetchCount} fetch${source.fetchCount === 1 ? "" : "es"}`
      : undefined,
    source.documentCount > 0 ? sourceUsageMetric(source) : undefined,
  ].filter((value): value is string => Boolean(value));
  return [
    source.provider ? `Web via ${humanize(source.provider)}` : "Web",
    ...operations,
  ].join(" · ");
}

function classifySource(input: {
  toolName: string;
  provider?: string;
  pluginSlug?: string;
  pluginLabel?: string;
  resolveToolPlugin?: ResolveToolPlugin;
}): Omit<
  RunSourceUsage,
  "callCount" | "documentCount" | "searchCount" | "fetchCount"
> | null {
  if (
    input.toolName === "twin_governed_product_query" ||
    input.toolName === "twin_plan_and_retrieve_governed_evidence"
  ) {
    return { key: "twin", kind: "twin", label: "Twin" };
  }
  if (
    input.toolName === "twin_web_search" ||
    input.toolName === "twin_google_grounded_answer" ||
    input.toolName === "twin_web_fetch" ||
    input.toolName === "mlx_web_search" ||
    input.toolName === "mlx_web_fetch"
  ) {
    const provider = input.provider?.toLowerCase();
    return {
      key: `web:${provider ?? "default"}`,
      kind: "web",
      label: "Web",
      provider,
    };
  }
  if (input.pluginSlug) {
    return {
      key: `plugin:${input.pluginSlug}`,
      kind: "plugin",
      label: pluginSourceLabel(input.pluginSlug, input.pluginLabel),
      pluginSlug: input.pluginSlug,
    };
  }
  if (WORKSPACE_SOURCE_TOOL.test(input.toolName)) {
    return { key: "workspace", kind: "workspace", label: "Workspace" };
  }
  if (
    input.toolName === "load_memory_context" ||
    input.toolName === "search_episodic_memory"
  ) {
    return { key: "memory", kind: "memory", label: "Memory" };
  }
  // Last resort: registry resolution by tool name, so CLI-lane tool events
  // without a payload pluginSlug still attribute to their connector. Every
  // name-based rule above keeps precedence.
  const resolved = input.resolveToolPlugin?.(input.toolName);
  if (resolved) {
    return {
      key: `plugin:${resolved.pluginSlug}`,
      kind: "plugin",
      label: pluginSourceLabel(resolved.pluginSlug, resolved.pluginLabel),
      pluginSlug: resolved.pluginSlug,
    };
  }
  return null;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readCount(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : 0;
}

function humanize(value: string): string {
  const normalized = value.replace(/[-_]+/g, " ").trim();
  return normalized
    ? normalized.replace(/\b\w/g, (character) => character.toUpperCase())
    : value;
}
